import { Controller, type FieldValues } from 'react-hook-form';

import { ButtonGroup } from '@/components/ui/button-group';
import { cn } from '@/lib/utils';
import { type CommonFormFieldProps, spanToClassName } from './form-input';

type FormSwitchProps<T extends FieldValues> = CommonFormFieldProps<T> & {
  description?: string;
  horizontal?: boolean;
  onLabel?: string;
  offLabel?: string;
};

export function FormSwitch<T extends FieldValues>({
  name,
  control,
  label,
  description,

  sm,
  md,
  lg,

  required,
  disabled,
  onLabel = 'Sim',
  offLabel = 'Não',
  className,
}: FormSwitchProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabled}
      render={({ field, fieldState }) => (
        <div className={cn(spanToClassName(sm, md, lg), className)}>
          <div
            className={cn(
              'flex h-[66px] min-w-0 flex-col justify-center gap-1.5 rounded-[14px] border border-border bg-field px-[18px] py-[13px]',
              fieldState.invalid && 'border-destructive',
            )}
          >
            <label htmlFor={name} className="col-label text-muted-foreground">
              {label} {required && <span className="text-sand">*</span>}
            </label>

            <ButtonGroup>
              <button
                id={name}
                type="button"
                disabled={field.disabled}
                onClick={() => field.onChange(false)}
                className={cn(
                  'h-8 flex-1 rounded-[10px] border px-4 text-[13px] font-semibold transition-colors',
                  !field.value
                    ? 'border-destructive/40 bg-destructive-soft text-destructive'
                    : 'border-border-strong bg-transparent text-muted-foreground hover:bg-muted',
                )}
              >
                {offLabel}
              </button>
              <button
                type="button"
                disabled={field.disabled}
                onClick={() => field.onChange(true)}
                className={cn(
                  'h-8 flex-1 rounded-[10px] border px-4 text-[13px] font-semibold transition-colors',
                  field.value
                    ? 'border-olive-line/40 bg-primary-soft text-olive-ink'
                    : 'border-border-strong bg-transparent text-muted-foreground hover:bg-muted',
                )}
              >
                {onLabel}
              </button>
            </ButtonGroup>
          </div>

          {description && (
            <p className="mt-1.5 text-xs text-muted-foreground">{description}</p>
          )}

          {fieldState.error && (
            <p className="mt-1.5 text-xs font-medium text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
